import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { useThemeStore, THEMES, type ThemeName, type ColorMode } from '../stores/themeStore'
import { useQuery } from '@tanstack/react-query'
import { api, apiPut, apiPost, apiDelete, API_BASE } from '../lib/api'
import type { DashboardLayoutPreset } from '../stores/dashboardLayoutStore'
import { ConfirmDialog } from '../components/ConfirmDialog'
import '../styles/profile.css'
import '../styles/ui.css'

const THEME_SWATCHES: Record<ThemeName, { dark: string[]; light: string[] }> = {
    professional: {
        dark: ['#4A2C82', '#6B44B8', '#D6134D', '#0B0B10', '#1A1A24'],
        light: ['#4A2C82', '#5B38A2', '#C4113F', '#FFFFFF', '#FFFFFF'],
    },
    bold: {
        dark: ['#8B5CF6', '#A78BFA', '#EC4899', '#030712', '#1E293B'],
        light: ['#7C3AED', '#8B5CF6', '#DB2777', '#FFFFFF', '#F1F5F9'],
    },
    playful: {
        dark: ['#E040A0', '#F472B6', '#FB923C', '#0E0A14', '#1E1830'],
        light: ['#DB2777', '#EC4899', '#EA580C', '#FFF7F9', '#FFFFFF'],
    },
    muted: {
        dark: ['#888888', '#AAAAAA', '#A1A1AA', '#121212', '#27272A'],
        light: ['#52525B', '#71717A', '#A1A1AA', '#FAFAFA', '#FFFFFF'],
    },
}

export default function MyProfilePage() {
    const user = useAuthStore(s => s.user)
    const token = useAuthStore(s => s.accessToken)
    const {
        theme: userTheme,
        colorMode: userColorMode,
        siteDefaultTheme,
        siteDefaultColorMode,
        setTheme,
        setColorMode,
        getEffectiveTheme,
        getEffectiveColorMode,
    } = useThemeStore()

    const effectiveTheme = getEffectiveTheme()
    const effectiveColorMode = getEffectiveColorMode()

    const [saving, setSaving] = useState(false)
    const [saved, setSaved] = useState(false)

    // Notification preferences. The Roadmap-submission toggle is for HeadOffice/SuperAdmin; the
    // access-request toggle is for anyone who can approve them (StoreManager+); the email frequency
    // applies to anyone (as a voter or submitter).
    const roadmapAdmin = user?.role === 'HeadOffice' || user?.role === 'SuperAdmin'
    const canApproveAccess = ['StoreManager', 'StoreOwner', 'HeadOffice', 'SuperAdmin'].includes(user?.role ?? '')
    const [subscribeToRoadmap, setSubscribeToRoadmap] = useState(false)
    const [subscribeToAccess, setSubscribeToAccess] = useState(true)
    const [notifyFrequency, setNotifyFrequency] = useState('Immediate')

    useEffect(() => {
        if (!token) return
        api('/api/me/preferences', token)
            .then((p: any) => {
                setSubscribeToRoadmap(!!p?.subscribeToRoadmapSubmissions)
                setSubscribeToAccess(p?.subscribeToAccessRequests !== false) // default on
                if (p?.notificationFrequency) setNotifyFrequency(p.notificationFrequency)
            })
            .catch(() => { /* keep defaults */ })
    }, [token])

    // Persist a notification preference. Theme values are included because the
    // preferences endpoint clears them when omitted.
    const persistNotifyPrefs = async (patch: {
        subscribeToRoadmapSubmissions?: boolean; notificationFrequency?: string; subscribeToAccessRequests?: boolean
    }) => {
        if (!token) return
        setSaving(true)
        try {
            await apiPut('/api/me/preferences', token, {
                preferredTheme: userTheme,
                preferredColorMode: userColorMode,
                ...patch,
            })
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
            return true
        } catch {
            return false
        } finally {
            setSaving(false)
        }
    }

    const handleToggleRoadmapSub = async (value: boolean) => {
        setSubscribeToRoadmap(value) // optimistic
        const ok = await persistNotifyPrefs({ subscribeToRoadmapSubmissions: value })
        if (ok === false) setSubscribeToRoadmap(!value) // revert on failure
    }

    const handleToggleAccessSub = async (value: boolean) => {
        setSubscribeToAccess(value) // optimistic
        const ok = await persistNotifyPrefs({ subscribeToAccessRequests: value })
        if (ok === false) setSubscribeToAccess(!value) // revert on failure
    }

    const handleFrequencyChange = async (value: string) => {
        const prev = notifyFrequency
        setNotifyFrequency(value) // optimistic
        const ok = await persistNotifyPrefs({ notificationFrequency: value })
        if (ok === false) setNotifyFrequency(prev) // revert on failure
    }

    const initials = user?.displayName
        .split(' ')
        .map(n => n[0])
        .slice(0, 2)
        .join('')
        .toUpperCase() ?? '?'

    const handleThemeChange = (themeName: ThemeName) => {
        setTheme(themeName)
        persistPreferences(themeName, userColorMode)
    }

    const handleColorModeToggle = () => {
        const newMode: ColorMode = effectiveColorMode === 'dark' ? 'light' : 'dark'
        setColorMode(newMode)
        persistPreferences(userTheme, newMode)
    }

    const handleUseSiteDefault = () => {
        setTheme(null)
        setColorMode(null)
        persistPreferences(null, null)
    }

    const persistPreferences = async (theme: ThemeName | null, colorMode: ColorMode | null) => {
        if (!token) return
        setSaving(true)
        try {
            await apiPut('/api/me/preferences', token, {
                preferredTheme: theme,
                preferredColorMode: colorMode,
            })
            setSaved(true)
            setTimeout(() => setSaved(false), 2000)
        } catch {
            /* silent — local state is already set */
        } finally {
            setSaving(false)
        }
    }

    return (
        <>
            <div className="page-header">
                <h1>My Profile</h1>
                <p>Manage your account settings and preferences</p>
            </div>

            <div className="profile-page">
                {/* User Info */}
                <div className="profile-section">
                    <div className="profile-section-title">Account</div>
                    <div className="profile-user-card">
                        {user?.avatarUrl ? (
                            <img
                                src={user.avatarUrl}
                                alt={user.displayName}
                                className="profile-avatar"
                                referrerPolicy="no-referrer"
                            />
                        ) : (
                            <div className="profile-avatar-fallback">{initials}</div>
                        )}
                        <div className="profile-user-info">
                            <div className="profile-user-name">{user?.displayName}</div>
                            <div className="profile-user-email">{user?.email}</div>
                            <div className="profile-user-role">
                                <span className={`badge badge-${(user?.role ?? 'unassigned').toLowerCase()}`}>
                                    {user?.role}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Appearance */}
                <div className="profile-section">
                    <div className="profile-section-title">
                        Appearance
                        {saved && <span className="settings-save-status"> ✓ Saved</span>}
                        {saving && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginLeft: 'var(--space-2)' }}>Saving…</span>}
                    </div>

                    {/* Theme Grid */}
                    <div className="theme-picker-grid">
                        {THEMES.map(t => (
                            <div
                                key={t.name}
                                className={`theme-card${effectiveTheme === t.name ? ' selected' : ''}`}
                                onClick={() => handleThemeChange(t.name)}
                            >
                                <div className="theme-card-preview">
                                    {THEME_SWATCHES[t.name][effectiveColorMode].map((color, i) => (
                                        <div key={i} className="swatch" style={{ background: color }} />
                                    ))}
                                </div>
                                <div className="theme-card-name">{t.label}</div>
                                <div className="theme-card-desc">{t.description}</div>
                            </div>
                        ))}
                    </div>

                    {/* Color mode toggle */}
                    <div className="color-mode-row">
                        <div className="color-mode-labels">
                            <span className={`color-mode-label${effectiveColorMode === 'light' ? ' active' : ''}`}>☀️ Light</span>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={effectiveColorMode === 'dark'}
                                    onChange={handleColorModeToggle}
                                />
                                <span className="toggle-slider" />
                            </label>
                            <span className={`color-mode-label${effectiveColorMode === 'dark' ? ' active' : ''}`}>🌙 Dark</span>
                        </div>
                    </div>

                    {/* Use Site Default */}
                    {(userTheme !== null || userColorMode !== null) && (
                        <div className="theme-default-hint">
                            <span>Site default: {siteDefaultTheme} / {siteDefaultColorMode}</span>
                            <button
                                className="btn btn-secondary"
                                style={{ fontSize: 'var(--text-xs)', padding: '2px 8px' }}
                                onClick={handleUseSiteDefault}
                            >
                                Use Site Default
                            </button>
                        </div>
                    )}
                </div>

                {/* Notifications */}
                <div className="profile-section">
                    <div className="profile-section-title">
                        Notifications
                        {saved && <span className="settings-save-status"> ✓ Saved</span>}
                    </div>

                    <div className="profile-toggle-row">
                        <div>
                            <div className="profile-toggle-label">Roadmap email frequency</div>
                            <div className="profile-toggle-hint">
                                How often to receive roadmap update emails. <strong>Immediate</strong> sends each as it
                                happens; <strong>Daily</strong> / <strong>Weekly</strong> batch them into one digest.
                            </div>
                        </div>
                        <select
                            className="form-select"
                            style={{ maxWidth: 160 }}
                            value={notifyFrequency}
                            onChange={e => handleFrequencyChange(e.target.value)}
                            disabled={saving}
                        >
                            <option value="Immediate">Immediate</option>
                            <option value="Daily">Daily digest</option>
                            <option value="Weekly">Weekly digest</option>
                        </select>
                    </div>

                    {canApproveAccess && (
                        <div className="profile-toggle-row">
                            <div>
                                <div className="profile-toggle-label">Access requests</div>
                                <div className="profile-toggle-hint">
                                    Email me when a new store access request needs my review.
                                </div>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={subscribeToAccess}
                                    onChange={e => handleToggleAccessSub(e.target.checked)}
                                    disabled={saving}
                                />
                                <span className="toggle-slider" />
                            </label>
                        </div>
                    )}

                    {roadmapAdmin && (
                        <div className="profile-toggle-row">
                            <div>
                                <div className="profile-toggle-label">Subscribe to Roadmap Submissions</div>
                                <div className="profile-toggle-hint">
                                    Email me whenever a new idea is submitted to the roadmap for review.
                                </div>
                            </div>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={subscribeToRoadmap}
                                    onChange={e => handleToggleRoadmapSub(e.target.checked)}
                                    disabled={saving}
                                />
                                <span className="toggle-slider" />
                            </label>
                        </div>
                    )}
                </div>

                {/* Dashboard Layouts */}
                <div className="profile-section">
                    <div className="profile-section-title">Dashboard Layout Presets</div>
                    <LayoutPresetManager />
                </div>
            </div>
        </>
    )
}

function LayoutPresetManager() {
    const token = useAuthStore(s => s.accessToken)
    const [dashboardType, setDashboardType] = useState('daily')
    const [renamingId, setRenamingId] = useState<string | null>(null)
    const [renameName, setRenameName] = useState('')
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)

    const { data: layoutsData, refetch } = useQuery<{ layouts: DashboardLayoutPreset[] }>({
        queryKey: ['dashboard-layouts', dashboardType],
        queryFn: () => api(`/api/dashboard/layouts?type=${dashboardType}`, token),
        enabled: !!token
    })

    const layouts = layoutsData?.layouts || []
    const hasUserDefault = layouts.some(l => l.isDefault)

    const handleSetSystemDefault = async () => {
        const def = layouts.find(l => l.isDefault)
        if (def && token) {
            await apiPut(`/api/dashboard/layout/${def.id}`, token, { isDefault: false })
            refetch()
        }
    }

    const handleSetUserDefault = async (id: string) => {
        if (!token) return
        await apiPut(`/api/dashboard/layout/${id}`, token, { isDefault: true })
        refetch()
    }

    const handleDelete = async (id: string) => {
        if (!token) return
        await apiDelete(`/api/dashboard/layout/${id}`, token)
        refetch()
    }

    const handleCopy = async (id: string) => {
        if (!token) return
        // Fetch full layout
        const layoutData = await api(`/api/dashboard/layout?type=${dashboardType}&id=${id}`, token)
        if (layoutData?.layoutJson) {
            await apiPost('/api/dashboard/layout', token, {
                layoutJson: layoutData.layoutJson,
                dashboardType,
                name: (layoutData.name || 'Layout') + ' (Copy)',
                isDefault: false
            })
            refetch()
        }
    }

    const handleRenameStart = (l: DashboardLayoutPreset) => {
        setRenamingId(l.id)
        setRenameName(l.name || 'Layout')
    }

    const handleRenameSubmit = async (id: string) => {
        if (!token) return
        await apiPut(`/api/dashboard/layout/${id}`, token, { name: renameName })
        setRenamingId(null)
        refetch()
    }

    const handleMove = async (id: string, dir: 'up' | 'down') => {
        if (!token) return
        const idx = layouts.findIndex(l => l.id === id)
        if (dir === 'up' && idx > 0) {
            // Swap sort orders
            const a = layouts[idx]
            const b = layouts[idx - 1]
            await apiPut(`/api/dashboard/layout/${a.id}`, token, { sortOrder: b.sortOrder })
            await apiPut(`/api/dashboard/layout/${b.id}`, token, { sortOrder: a.sortOrder })
            refetch()
        } else if (dir === 'down' && idx < layouts.length - 1) {
            const a = layouts[idx]
            const b = layouts[idx + 1]
            await apiPut(`/api/dashboard/layout/${a.id}`, token, { sortOrder: b.sortOrder })
            await apiPut(`/api/dashboard/layout/${b.id}`, token, { sortOrder: a.sortOrder })
            refetch()
        }
    }

    return (
        <div>
            <div className="seg-control" style={{ marginBottom: '1rem', display: 'inline-flex' }}>
                <button className={dashboardType === 'daily' ? 'active' : ''} onClick={() => setDashboardType('daily')}>Daily Dashboard</button>
                <button className={dashboardType === 'monthly' ? 'active' : ''} onClick={() => setDashboardType('monthly')}>Monthly Dashboard</button>
                <button className={dashboardType === 'custom-range' ? 'active' : ''} onClick={() => setDashboardType('custom-range')}>Custom Dashboard</button>
            </div>

            {/* Homepage Dashboard Toggle */}
            <HomepageDashboardToggle dashboardType={dashboardType} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {/* System Default Row */}
                <div style={{ padding: '12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--color-surface)' }}>
                    <div>
                        <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>System Default</div>
                        <div style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>Read-only system wide default</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {!hasUserDefault ? (
                            <span className="badge badge-starburst">🌟 Your Default</span>
                        ) : (
                            <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 8px' }} onClick={handleSetSystemDefault}>
                                Set as Default
                            </button>
                        )}
                    </div>
                </div>

                {/* User Presets */}
                {layouts.map((l, idx) => (
                    <div key={l.id} style={{ padding: '12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--color-bg)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                            {/* Reorder Controls */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <button className="toolbar-refresh-btn" style={{ padding: '2px', fontSize: '10px' }} disabled={idx === 0} onClick={() => handleMove(l.id, 'up')}>▲</button>
                                <button className="toolbar-refresh-btn" style={{ padding: '2px', fontSize: '10px' }} disabled={idx === layouts.length - 1} onClick={() => handleMove(l.id, 'down')}>▼</button>
                            </div>
                            
                            {/* Name */}
                            {renamingId === l.id ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input 
                                        type="text" 
                                        value={renameName} 
                                        onChange={e => setRenameName(e.target.value)}
                                        style={{ padding: '4px', background: 'var(--color-surface)', color: 'var(--color-text)', border: '1px solid var(--color-border)', borderRadius: '4px' }}
                                        autoFocus
                                        onKeyDown={e => e.key === 'Enter' && handleRenameSubmit(l.id)}
                                    />
                                    <button className="btn btn-primary" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => handleRenameSubmit(l.id)}>Save</button>
                                    <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} onClick={() => setRenamingId(null)}>Cancel</button>
                                </div>
                            ) : (
                                <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>{l.name}</div>
                            )}
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {l.isDefault ? (
                                <span className="badge badge-starburst">🌟 Your Default</span>
                            ) : (
                                <button className="btn btn-secondary" style={{ fontSize: '12px', padding: '4px 8px' }} onClick={() => handleSetUserDefault(l.id)}>
                                    Set as Default
                                </button>
                            )}

                            <span style={{ width: '1px', height: '16px', background: 'var(--color-border)', margin: '0 4px' }} />
                            
                            <button className="toolbar-refresh-btn" style={{ fontSize: '14px', padding: '4px' }} title="Rename" onClick={() => handleRenameStart(l)}>✏️</button>
                            <button className="toolbar-refresh-btn" style={{ fontSize: '14px', padding: '4px' }} title="Duplicate" onClick={() => handleCopy(l.id)}>📄</button>
                            <button className="toolbar-refresh-btn" style={{ fontSize: '14px', padding: '4px', color: 'var(--color-error)' }} title="Delete" onClick={() => setConfirmDeleteId(l.id)}>🗑️</button>
                        </div>
                    </div>
                ))}
            </div>

            {confirmDeleteId && (
                <ConfirmDialog
                    title="Delete layout preset?"
                    message="Are you sure you want to delete this layout preset?"
                    confirmLabel="Delete"
                    danger
                    onCancel={() => setConfirmDeleteId(null)}
                    onConfirm={() => { const id = confirmDeleteId; setConfirmDeleteId(null); handleDelete(id) }}
                />
            )}
        </div>
    )
}

/** Maps the layout preset tab value to the homepage API value */
const TAB_TO_HOMEPAGE: Record<string, string> = {
    'daily': 'daily',
    'monthly': 'monthly',
    'custom-range': 'custom',
}

function HomepageDashboardToggle({ dashboardType }: { dashboardType: string }) {
    const token = useAuthStore(s => s.accessToken)
    const [homepage, setHomepage] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)

    const homepageType = TAB_TO_HOMEPAGE[dashboardType] ?? 'daily'
    const isHomepage = (homepage ?? 'daily') === homepageType

    useEffect(() => {
        if (!token) return
        fetch(API_BASE + '/api/me/preferences', {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.ok ? r.json() : null)
            .then(data => setHomepage(data?.homepageDashboard ?? null))
            .catch(() => {})
    }, [token])

    const handleToggle = async () => {
        if (!token) return
        setSaving(true)
        try {
            const body = isHomepage
                ? { clearHomepage: true }
                : { homepageDashboard: homepageType }
            const res = await fetch(API_BASE + '/api/me/preferences', {
                method: 'PUT',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(body)
            })
            if (res.ok) {
                const data = await res.json()
                setHomepage(data.homepageDashboard ?? null)
            }
        } catch { /* silent */ }
        finally { setSaving(false) }
    }

    return (
        <div style={{
            padding: '10px 12px',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'var(--color-surface)',
            marginBottom: '12px',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '16px' }}>🏠</span>
                <span style={{ fontWeight: 500, fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
                    Homepage Dashboard
                </span>
                <span style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
                    — shown on login
                </span>
            </div>
            <label className="toggle-switch" style={{ opacity: saving ? 0.5 : 1 }}>
                <input
                    type="checkbox"
                    checked={isHomepage}
                    onChange={handleToggle}
                    disabled={saving}
                />
                <span className="toggle-slider" />
            </label>
        </div>
    )
}
